import { useMemo } from 'react'

import { AgChartOptions, AgLineSeriesOptions } from 'ag-charts-community'

import { cn } from '@fortem-monorepo/js-utilities'
import { CustomAgChart } from '@fortem-monorepo/react-ag'
import { ICReport } from '@fortem-monorepo/web-sdk'

import { ICReportChartPrimitive } from '@/pages/funds/ic-report/components/primitives/types'
import { income } from '@/react-query/queries/ic-report/hooks'

export interface LockedYieldChartsProps extends ICReportChartPrimitive {}

export const LockedYieldCharts: React.FC<LockedYieldChartsProps> = ({ params, options }) => {
  const query = income.useLockedYield(params)

  const dataEntries = useMemo(() => {
    return Object.entries(query.data ?? [])
  }, [query.data])

  return (
    <div
      className={cn('grid', {
        ['grid-cols-2']: dataEntries.length == 2,
      })}
    >
      {dataEntries.map(([title, obj]) => {
        return (
          <LockedYieldChart
            chart={obj}
            options={options}
            title={dataEntries.length > 1 ? title : undefined}
            key={title}
          />
        )
      })}
    </div>
  )
}

interface LockedYieldChartProps {
  title?: string
  chart: ICReport.GetLockedYieldData['string']
  options: ICReportChartPrimitive['options']
}

export const LockedYieldChart: React.FC<LockedYieldChartProps> = ({ title, chart, options }) => {
  const formattedData = useMemo(() => {
    return chart.data.map((item) => {
      return {
        ...item,
        index: new Date(item.index),
      }
    })
  }, [])

  const yKeys = Object.keys(formattedData?.[0] ?? {}).filter((key) => key !== 'index')

  const mergedOptions: AgChartOptions = {
    title: title
      ? {
          text: title,
        }
      : undefined,
    height: 500,
    zoom: {
      enabled: true,
      enablePanning: true,
      enableScrolling: true,
      anchorPointX: 'pointer',
      anchorPointY: 'pointer',
    },
    navigator: {
      enabled: true,
    },
    data: formattedData,
    series: yKeys.map((yKey) => {
      return {
        type: 'line',
        xKey: 'index',
        yKey,
        yName: yKey,
        marker: {
          size: 0,
        },
      } satisfies AgLineSeriesOptions
    }),
    axes: [
      {
        type: 'time',
        position: 'bottom',
        nice: false,
        parentLevel: {
          enabled: true,
        },
      },
      {
        type: 'number',
        position: 'left',
        label: {
          formatter: (data) => {
            return `${data?.value?.toFixed(2)}%`
          },
        },
      },
    ],
    ...options,
  }

  return <CustomAgChart options={mergedOptions} />
}
